import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './TransactionDashboard.css';
import { TransactionsApi } from '../../../api/common/apiIndex';
import StatusBadge from './StatusBadge';
import { useAuth } from '../../../context/AuthProvider';
import useDebounce from '../hooks/useDebounce';
import Filters from '../businessUtilities/Filters';
import countries from 'i18n-iso-countries';
import { useIsDarkTheme } from '../hooks/useIsDarkTheme';
import Loader from '../../common/commonUtils/Loader';
import { FiRefreshCw } from 'react-icons/fi';
import { trackEvent } from '../../../utils/analytics';

try { countries.registerLocale(require('i18n-iso-countries/langs/en.json')); } catch { }

const IN_PROGRESS = ['PROCESSING', 'READY'];
const PAID = ['PAID'];

function useIsMdUp() {
  const query = "(min-width: 768px)";
  const get = () =>
    typeof window !== "undefined" && window.matchMedia(query).matches;
  const [isMdUp, setIsMdUp] = useState(get);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMdUp(e.matches);
    mql.addEventListener ? mql.addEventListener("change", onChange) : mql.addListener(onChange);
    return () => {
      mql.removeEventListener ? mql.removeEventListener("change", onChange) : mql.removeListener(onChange);
    };
  }, []);
  return isMdUp;
}

const TransactionDashboard = () => {
  const { role } = useAuth();
  const isAgent = role === 'AGENT';
  const assignedTo = isAgent ? 'me' : undefined;

  const [view, setView] = useState('in-progress');
  const [page, setPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [country, setCountry] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightId, setHighlightId] = useState(null);
  const isMdUp = useIsMdUp();
  const isDark = useIsDarkTheme();

  const q = useDebounce(searchTerm, 350);
  const size = 11;
  const sort = 'createdAt,desc';

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const updatedId = location.state?.updatedId;
  const sendAgain = useCallback(
    (recipientId, txUuid, source = "card") => {
      if (!recipientId) return;
      trackEvent("send_again_clicked", {
        source,
        recipientId: String(recipientId),
        txId: txUuid || undefined,
      });
      const params = new URLSearchParams();
      params.set("recipientId", String(recipientId));
      if (txUuid) params.set("prefillFromTx", txUuid);
      navigate(`/home/transfer/new?${params.toString()}`);
    },
    [navigate]
  );

  const statusFilter = useMemo(() => {
    if (view === 'all') return undefined;
    if (view === 'in-progress') return IN_PROGRESS;
    return PAID;
  }, [view]);

  useEffect(() => { setPage(0); }, [view, q, country]);

  useEffect(() => {
    if (!isMdUp) setShowFilters(true);
  }, [isMdUp]);

  useEffect(() => {
    if (!updatedId) return;

    setHighlightId(updatedId);

    const refreshQueries = async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tx-count'] }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
      ]);

      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['tx-count'], type: 'all' }),
        queryClient.refetchQueries({ queryKey: ['transactions'], type: 'all' }),
      ]);
    };

    void refreshQueries();

    navigate(location.pathname, {
      replace: true,
      state: { ...location.state, updatedId: undefined },
    });
  }, [updatedId, queryClient, navigate, location.pathname, location.state]);

  const countResults = useQueries({
    queries: [
      {
        queryKey: ['tx-count', { scope: assignedTo ? 'mine' : 'all', status: 'ALL', q, country }],
        queryFn: async () => {
          const res = await TransactionsApi.list({ page: 0, size: 1, assignedTo, q, country });
          return res.totalElements ?? 0;
        },
        staleTime: 1000 * 60 * 5,
      },
      {
        queryKey: ['tx-count', { scope: assignedTo ? 'mine' : 'all', status: 'IN_PROGRESS', q, country }],
        queryFn: async () => {
          const res = await TransactionsApi.list({ page: 0, size: 1, assignedTo, status: IN_PROGRESS, q, country });
          return res.totalElements ?? 0;
        },
        staleTime: 1000 * 60 * 5,
      },
      {
        queryKey: ['tx-count', { scope: assignedTo ? 'mine' : 'all', status: 'PAID', q, country }],
        queryFn: async () => {
          const res = await TransactionsApi.list({ page: 0, size: 1, assignedTo, status: PAID, q, country });
          return res.totalElements ?? 0;
        },
        staleTime: 1000 * 60 * 5,
      },
    ],
  });

  const allCount = countResults[0]?.data ?? 0;
  const inProgressCount = countResults[1]?.data ?? 0;
  const paidCount = countResults[2]?.data ?? 0;
  const countsLoading = countResults.some((qr) => qr.isLoading || qr.isFetching);

  const {
    data,
    isLoading: loading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['transactions', { page, size, sort, scope: assignedTo ? 'mine' : 'all', status: statusFilter, q, country }],
    queryFn: () =>
      TransactionsApi.list({
        page,
        size,
        sort,
        assignedTo,
        status: statusFilter,
        q,
        country,
      }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
    placeholderData: (prev) => prev,
  });

  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const transactions = useMemo(() => data?.content ?? [], [data?.content]);

  const countryLabel = useMemo(() => {
    if (!country) return "";
    const normalized = country.length === 2 ? country.toUpperCase() : countries.getAlpha2Code(country, "en");
    if (!normalized) return country;
    try {
      return countries.getName(normalized, "en") || normalized;
    } catch {
      return normalized;
    }
  }, [country]);

  const {
    data: allCountryNames = [],
    isLoading: countriesLoading,
  } = useQuery({
    queryKey: ['tx-country-facets', { scope: assignedTo ? 'mine' : 'all', status: statusFilter, q }],
    queryFn: async ({ signal }) => {
      const seen = new Set();
      let p = 0;
      const pageSize = 500;
      while (true) {
        const res = await TransactionsApi.list({
          page: p,
          size: pageSize,
          sort: 'createdAt,desc',
          assignedTo,
          status: statusFilter,
          q,
          signal,
        });

        for (const t of res?.content || []) {
          if (t?.recipientCountry) seen.add(t.recipientCountry);
        }

        const tp = res?.totalPages ?? 0;
        if (p + 1 >= tp) break;
        p += 1;
      }
      return Array.from(seen).sort((a, b) => a.localeCompare(b));
    },
    staleTime: 1000 * 60 * 5,
    enabled: showFilters,
  });

  const clearFilters = () => {
    setCountry('');
    setSearchTerm('');
  };

  if (loading && !data) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Loader text="Loading..." />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        Could not load transactions
      </div>
    );
  }

  const tabs = [
    { type: 'all', label: 'All Transfers', count: allCount },
    { type: 'in-progress', label: 'In Progress', count: inProgressCount },
    { type: 'paid', label: 'Paid', count: paidCount },
  ];

  return (
    <div className="">
      <div className="sticky-top w-100 mb-3" >
        <div className="card border rounded-4 shadow-sm mb-0">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md" style={{ minWidth: 0 }}>
                <div className="nav nav-pills gap-2 p-2 d-flex flex-nowrap overflow-auto">
                  {tabs.map(({ type, label, count }) => (
                    <button
                      key={type}
                      type="button"
                      className={`tab-btn rounded-pill d-flex align-items-center justify-content-between px-3 py-2 flex-shrink-0 ${view === type ? 'active' : ''}`}
                      aria-pressed={view === type}
                      onClick={() => setView(type)}
                      disabled={countsLoading && view !== type}
                    >
                      <span className="me-2">{label}</span>
                      <span className={`badge rounded-pill ${view === type ? 'bg-light text-dark' : 'text-bg-light border'}`}>
                        {countsLoading ? '…' : count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-12 col-md-auto ms-md-auto">
                <button
                  className={`btn btn-outline-secondary btn-sm mt-2 rounded-pill d-none d-md-inline-flex align-items-center gap-2 w-100 w-md-auto ${isDark ? 'text-white' : ''}`}
                  onClick={() => setShowFilters((s) => !s)}
                  aria-expanded={showFilters}
                  aria-controls="tx-filters"
                >
                  <i className="bi bi-funnel" />
                  {showFilters ? "Hide filters" : "Show filters"}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="w-100 " id="tx-filters">
          <Filters
            id="tx-filters-inner"
            data={allCountryNames}
            getCountry={(x) => x}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedCountry={country}
            onCountryChange={setCountry}
          />
          {countriesLoading && <small className="text-muted">Loading countries…</small>}
        </div>
      )}

      <div className="w-100 d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2 px-3 px-md-4 mt-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {(!!country || !!q) && (
            <>
              <span className="badge text-bg-light border">Total: {totalElements}</span>
              <small className="text-muted">Filters:</small>
              {country && (
                <span className="badge text-bg-light border">Country: {countryLabel || country}</span>
              )}
              {q && <span className="badge text-bg-light border">Search: “{q}”</span>}
              <button className="btn btn-sm btn-link text-decoration-none" onClick={clearFilters}>
                Clear filters
              </button>
            </>
          )}
        </div>
      </div>

      <div className="transaction-dashboard d-flex flex-column align-items-center card border rounded-4 shadow-sm">
        <div className="transaction-card-wrapper w-100 p-2 p-md-4">
          {transactions.length > 0 ? (
            transactions.map((tx) => {
              const isHighlighted = highlightId === tx.id;
              const statusLabel = String(tx.status || "").toUpperCase();
              const canSendAgain = statusLabel === 'PAID' && tx.recipientId;
              return (
                <div
                  key={tx.id}
                  className={`transaction-card border rounded-4 ${isHighlighted ? 'highlight-card border-success shadow' : ''}`}
                  onClick={() => navigate(`/home/transaction/${tx.uuid}`)}
                  role="button"
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold d-flex align-items-center">
                        <img
                          src={`https://flagcdn.com/24x18/${tx.recipientCountry?.toLowerCase()}.png`}
                          alt={tx.recipientCountry || 'Country'}
                          className="me-2 rounded"
                          width={24}
                          height={18}
                          loading="lazy"
                        />
                        <span>{tx.recipientName}</span>
                      </div>
                      <div className="text-muted small">
                        Sent{' '}
                        {new Date(tx.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </div>

                    <div className="text-end">
                      <div className="fw-bold">
                        {Number(tx.receiveAmount).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        {tx.receiveCurrency}
                      </div>
                      <div className="mt-1 d-flex justify-content-end align-items-center gap-2">
                        <StatusBadge status={tx.status} />
                        {canSendAgain && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-pill"
                            onClick={(event) => {
                              event.stopPropagation();
                              sendAgain(tx.recipientId, tx.uuid, "card");
                            }}
                            title="Send again"
                            aria-label="Send again"
                          >
                            <FiRefreshCw />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body text-center text-muted">
                {(country || q) ? 'No transactions match the current filters.' : 'No transactions found.'}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex w-100 justify-content-end gap-2 p-md-4 align-items-center px-3 m-3">
          <button
            className="btn btn-outline-secondary btn-sm rounded-pill"
            disabled={page === 0 || isFetching}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </button>
          <span className="text-muted small">
            Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
            {isFetching ? ' …' : ''}
          </span>
          <button
            className="btn btn-outline-secondary btn-sm rounded-pill"
            disabled={page + 1 >= totalPages || isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDashboard;
